/**
 * MediaSourceSelector Component
 *
 * A unified selector for choosing the visual media source in videos.
 * Selection is EXCLUSIVE: either images (static) OR videos (motion).
 *
 * Video categories:
 * - Stock Videos (free, Pexels/Pixabay)
 * - YouTube Clips (free)
 * - AI Videos (T2V + I2V providers, opens sub-panel)
 * - AI Director (smart auto-selection per scene)
 */

import { memo, useState, useMemo, useCallback, useEffect } from 'react';
import { Image, Film, Sparkles, Brain } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useGeoRestriction } from '../../hooks/useGeoRestriction';
import { VeoOptionsPanel } from './media/VeoOptionsPanel';
import { KlingOptionsPanel } from './media/KlingOptionsPanel';
import { ViduOptionsPanel } from './media/ViduOptionsPanel';
import { Seedance2OptionsPanel } from './media/Seedance2OptionsPanel';
import { FooocusStyleOptions } from './media/FooocusStyleOptions';
import { ComfyUISDOptions } from './media/ComfyUISDOptions';
import { AIImagePromptOptions } from '../image/AIImagePromptOptions';
import type { LoraEntry } from './media/ComfyUISDOptions';
import {
  DeforumScheduleEditor,
  DEFAULT_DEFORUM_CONFIG,
  type DeforumConfig,
} from '../common/DeforumScheduleEditor';
import { VideoSourceCards } from './media/VideoSourceCards';
import { getVideoCategory, type VideoCategory } from './media/VideoSourceCards.utils';
import { AIVideoProviderPanel } from './media/AIVideoProviderPanel';
import { ImageSourceCards } from './media/ImageSourceCards';
import { ImageProviderSelector } from '../image/ImageProviderSelector';
import { CompactSelect } from '../common/CompactSelect';
import { ProviderPicker } from '../common/ProviderPicker';
import type { PickerOption } from '../common/ProviderPicker';

/**
 * Quality defaults (1-5) for I2V image providers.
 * Used when backend availability record does not supply quality info.
 */
const I2V_IMAGE_QUALITY_DEFAULTS: Record<string, 1 | 2 | 3 | 4 | 5> = {
  fooocus: 4,
  'comfyui-sd': 3,
  'z-image-turbo': 4,
  'black-forest-labs-flux-1-schnell': 5,
  'seedream-v4-t2i': 5,
  'qwen-image-t2i': 4,
};

import {
  VIDEO_SOURCES,
  VIDEO_SOURCE_INFO,
  STOCK_VIDEO_PROVIDERS,
  DEFAULT_STOCK_VIDEO_PROVIDER,
  calculateVeoCost,
  type VideoSource,
  type VeoConfig,
  type StockVideoProvider,
} from '../../constants/videoSources';
import { useImageProviderAvailability } from '../../hooks/useImageProviderAvailability';

import {
  DEFAULT_KLING_CONFIG,
  calculateKlingCost,
  type KlingConfig,
} from '../../constants/klingVideo';

import { DEFAULT_VIDU_CONFIG, type ViduConfig } from '../../constants/viduVideo';

import { DEFAULT_SEEDANCE2_CONFIG, type Seedance2Config } from '../../constants/seedance2Video';

import {
  STOCK_IMAGE_PROVIDERS,
  AI_IMAGE_PROVIDERS,
  DEFAULT_STOCK_IMAGE_PROVIDER,
  DEFAULT_AI_IMAGE_PROVIDER,
  getProviderFeatures,
  type ImageSourceType,
  type StockImageProvider,
  type AIImageProvider,
} from '../../constants/imageSource';
import type { ImageProviderAvailability } from '../../types/videoProviders';

/** Media type: static images or motion video */
export type MediaType = 'images' | 'videos';

/** Source type within media: stock or AI-generated */
export type SourceType = 'stock' | 'ai';

/** Style selection mode for Fooocus */
export type FooocusStyleMode = 'auto' | 'manual';

export interface MediaSourceConfig {
  // Primary selection: images or videos
  media_type: MediaType;

  // For images
  image_source: ImageSourceType;
  /** AI image generator (used when image_source === 'ai') */
  image_provider: AIImageProvider;
  /** Stock image provider (used when image_source === 'stock'): pexels | pixabay */
  stock_image_provider: StockImageProvider;

  // For videos
  video_source: VideoSource;
  /** Stock video provider (used when video_source === 'stock'): pexels | pixabay */
  stock_video_provider: StockVideoProvider;

  // Fooocus AI styles (when image_source is 'ai')
  fooocus_style_mode: FooocusStyleMode;
  fooocus_styles: string[];
  fooocus_style_description: string;
  fooocus_model?: string;

  // ComfyUI SD 1.5 options (when image_provider is 'comfyui-sd')
  comfyui_sd_model?: string;
  comfyui_sd_steps?: number;
  comfyui_sd_loras?: LoraEntry[];

  // Global AI-image prompt controls (Fooocus / ComfyUI-SD). Shared with the
  // image tab; merged with the per-scene LLM prompt/negative at generation time.
  image_negative_prompt: string;
  image_use_suggested_quality: boolean;

  // Veo config (when video_source is 'ai_veo')
  veo_config: VeoConfig;

  // Kling config (when video_source is 'ai_kling')
  kling_config: KlingConfig;

  // Vidu config (when video_source is 'ai_vidu')
  vidu_config: ViduConfig;

  // Seedance2 config (when video_source is 'ai_seedance2')
  seedance2_config: Seedance2Config;

  // Deforum config (when video_source is 'ai_deforum')
  deforum_config: DeforumConfig;

  // I2V first-frame image generator (required when video_source.requiresImage is true)
  image_provider_for_video: string | null;
}

interface MediaSourceSelectorProps {
  config: MediaSourceConfig;
  onChange: (config: MediaSourceConfig) => void;
  durationMinutes: number;
  disabled?: boolean;
  className?: string;
  /** Target video resolution, e.g. "1920x1080" — forwarded to
   * DeforumScheduleEditor so its render orientation matches the video. */
  resolution?: string;
}

// =============================================================================
// I2V IMAGE PROVIDER PICKER
// Sub-component: replaces the hand-rolled 6-button grid.
// Emits only the factual LOCAL badge (routing_mode local_only + is_installed).
// Never forwards the raw avail.badge string (may contain banned marketing strings).
// =============================================================================

const I2V_IMAGE_PROVIDER_IDS = [
  'fooocus',
  'comfyui-sd',
  'z-image-turbo',
  'black-forest-labs-flux-1-schnell',
  'seedream-v4-t2i',
  'qwen-image-t2i',
] as const;

interface I2VImageProviderPickerProps {
  imgAvailability: Map<string, ImageProviderAvailability> | null;
  value: string | null;
  onChange: (providerId: string) => void;
  disabled?: boolean;
}

const I2VImageProviderPicker = memo(function I2VImageProviderPicker({
  imgAvailability,
  value,
  onChange,
  disabled = false,
}: I2VImageProviderPickerProps) {
  const pickerOptions: PickerOption[] = useMemo(
    () =>
      I2V_IMAGE_PROVIDER_IDS.map((pid) => {
        const avail = imgAvailability?.get(pid);
        const hasCloudFallback =
          avail?.routing_mode === 'hybrid' || avail?.routing_mode === 'cloud_only';
        const isUnavailable = avail !== undefined && !avail.is_installed && !hasCloudFallback;
        // LOCAL badge: emit whenever the provider is actually installed on this machine,
        // regardless of routing_mode. Fooocus is hybrid (local + cloud fallback) but still
        // runs locally for free when installed — it deserves the LOCAL badge.
        const isLocalInstalled = avail?.is_local === true && avail?.is_installed === true;
        // Pricing: "Free" for local-installed providers.
        // ImageProviderAvailability has no per-provider price field — leave undefined for cloud
        // so the card shows stars + badge without a confusing price string.
        const pricingStr: string | undefined = isLocalInstalled ? 'Free' : undefined;

        return {
          value: pid,
          label: avail?.display_name ?? pid,
          // Emit LOCAL badge only when the provider genuinely runs locally and is installed.
          // NEVER forward avail.badge — it may contain marketing strings (AUTO/FAST/etc.).
          badge: isLocalInstalled ? ({ text: 'LOCAL', tone: 'local' } as const) : undefined,
          pricing: pricingStr,
          quality: I2V_IMAGE_QUALITY_DEFAULTS[pid],
          disabled: isUnavailable,
          disabledReason: isUnavailable ? 'Not installed' : undefined,
          // Fooocus is universally available (has both local + cloud route)
          recommended: pid === 'fooocus',
        };
      }),
    [imgAvailability]
  );

  return (
    <ProviderPicker
      options={pickerOptions}
      value={value}
      onChange={onChange}
      disabled={disabled}
      sortKeys={[]}
      searchable={false}
      cardMinWidth={160}
    />
  );
});

export const MediaSourceSelector = memo(function MediaSourceSelector({
  config,
  onChange,
  durationMinutes,
  disabled = false,
  className = '',
  resolution,
}: MediaSourceSelectorProps) {
  const { t } = useTranslation();
  const { isFeatureAllowed } = useGeoRestriction();
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [activeCategory, setActiveCategory] = useState<VideoCategory>(
    getVideoCategory(config.video_source)
  );

  // Calculate estimated Veo cost
  const estimatedVeoCost = useMemo(() => {
    if (config.media_type !== 'videos' || config.video_source !== VIDEO_SOURCES.AI_VEO) return 0;
    const durationSeconds = Math.ceil(durationMinutes * 60);
    return calculateVeoCost(
      durationSeconds,
      config.veo_config.quality,
      config.veo_config.resolution
    );
  }, [
    config.media_type,
    config.video_source,
    durationMinutes,
    config.veo_config.quality,
    config.veo_config.resolution,
  ]);

  // Calculate estimated Kling cost
  const estimatedKlingCost = useMemo(() => {
    if (config.media_type !== 'videos' || config.video_source !== VIDEO_SOURCES.AI_KLING) return 0;
    const durationSeconds = Math.ceil(durationMinutes * 60);
    const klingConfig = config.kling_config ?? DEFAULT_KLING_CONFIG;
    const clipDuration = parseInt(klingConfig.duration);
    const numClips = Math.ceil(durationSeconds / clipDuration);
    const costPerClip = calculateKlingCost(klingConfig.duration, klingConfig.model);
    return numClips * costPerClip;
  }, [config.media_type, config.video_source, durationMinutes, config.kling_config]);

  const handleMediaTypeChange = (mediaType: MediaType) => {
    onChange({
      ...config,
      media_type: mediaType,
      // Reset video_source to stock when switching to images mode
      // to avoid stale AI video source leaking into cost estimation
      ...(mediaType === 'images' ? { video_source: VIDEO_SOURCES.STOCK } : {}),
    });
  };

  const handleImageSourceChange = (source: ImageSourceType) => {
    // image_provider only applies when source === 'ai' (AI generator).
    // stock_image_provider only applies when source === 'stock' (pexels/pixabay).
    // We seed defaults only if the corresponding field is empty.
    const updates: Partial<MediaSourceConfig> = { image_source: source };
    if (source === 'ai' && !config.image_provider) {
      updates.image_provider = DEFAULT_AI_IMAGE_PROVIDER;
    }
    if (source === 'stock' && !config.stock_image_provider) {
      updates.stock_image_provider = DEFAULT_STOCK_IMAGE_PROVIDER;
    }
    onChange({ ...config, ...updates });
  };

  const handleVideoSourceChange = useCallback(
    (source: VideoSource) => {
      // stock_video_provider is only meaningful when source === 'stock'.
      // For other sources the AI provider is encoded in video_source itself.
      const updates: Partial<MediaSourceConfig> = {
        video_source: source,
      };
      if (source === VIDEO_SOURCES.STOCK && !config.stock_video_provider) {
        updates.stock_video_provider = DEFAULT_STOCK_VIDEO_PROVIDER;
      }

      // Initialize Kling config with defaults when Kling is selected
      if (source === VIDEO_SOURCES.AI_KLING && !config.kling_config) {
        updates.kling_config = DEFAULT_KLING_CONFIG;
      }

      // Initialize Vidu config with defaults when Vidu is selected
      if (source === VIDEO_SOURCES.AI_VIDU && !config.vidu_config) {
        updates.vidu_config = DEFAULT_VIDU_CONFIG;
      }

      // Initialize Seedance2 config with defaults when Seedance2 is selected
      if (source === VIDEO_SOURCES.AI_SEEDANCE2 && !config.seedance2_config) {
        updates.seedance2_config = DEFAULT_SEEDANCE2_CONFIG;
      }

      // Initialize Deforum config with defaults when Deforum is selected
      if (source === VIDEO_SOURCES.AI_DEFORUM && !config.deforum_config) {
        updates.deforum_config = DEFAULT_DEFORUM_CONFIG;
      }

      // Update active category
      setActiveCategory(getVideoCategory(source));

      onChange({
        ...config,
        ...updates,
      });
    },
    [config, onChange]
  );

  const handleCategoryChange = useCallback((category: VideoCategory) => {
    setActiveCategory(category);
  }, []);

  const handleImageProviderChange = (provider: string) => {
    // Route to the right field based on current image_source so AI and stock
    // providers don't overwrite each other.
    if (config.image_source === 'stock') {
      onChange({ ...config, stock_image_provider: provider as StockImageProvider });
    } else {
      onChange({ ...config, image_provider: provider as AIImageProvider });
    }
  };

  const handleVeoConfigChange = (veoConfig: VeoConfig) => {
    onChange({
      ...config,
      veo_config: veoConfig,
    });
  };

  const handleKlingConfigChange = (klingConfig: KlingConfig) => {
    onChange({
      ...config,
      kling_config: klingConfig,
    });
  };

  const handleViduConfigChange = (viduConfig: ViduConfig) => {
    onChange({
      ...config,
      vidu_config: viduConfig,
    });
  };

  const handleSeedance2ConfigChange = (seedance2Config: Seedance2Config) => {
    onChange({ ...config, seedance2_config: seedance2Config });
  };

  const handleDeforumConfigChange = (deforumConfig: DeforumConfig) => {
    onChange({ ...config, deforum_config: deforumConfig });
  };

  const handleFooocusStyleModeChange = (mode: FooocusStyleMode) => {
    onChange({
      ...config,
      fooocus_style_mode: mode,
    });
  };

  const handleFooocusStylesChange = (styles: string[]) => {
    onChange({
      ...config,
      fooocus_styles: styles,
    });
  };

  const handleFooocusStyleDescriptionChange = (description: string) => {
    onChange({
      ...config,
      fooocus_style_description: description,
    });
  };

  const handleFooocusModelChange = (model: string) => {
    onChange({
      ...config,
      fooocus_model: model,
    });
  };

  const handleComfyuiModelChange = (model: string) => {
    onChange({ ...config, comfyui_sd_model: model });
  };

  const handleComfyuiStepsChange = (steps: number) => {
    onChange({ ...config, comfyui_sd_steps: steps });
  };

  const handleComfyuiLorasChange = (loras: LoraEntry[]) => {
    onChange({ ...config, comfyui_sd_loras: loras });
  };

  // Get current image provider options, filter NSFW for restricted regions
  const nsfwAllowed = isFeatureAllowed('nsfw_models');
  const imageProviders = useMemo(() => {
    const providers = config.image_source === 'stock' ? STOCK_IMAGE_PROVIDERS : AI_IMAGE_PROVIDERS;
    if (nsfwAllowed) return providers;
    return providers.filter((p) => !p.nsfw);
  }, [config.image_source, nsfwAllowed]);

  const showFooocusOptions =
    config.media_type === 'images' &&
    config.image_source === 'ai' &&
    config.image_provider === 'fooocus';

  // Check if provider-specific options should be shown
  const showVeoOptions =
    config.media_type === 'videos' && config.video_source === VIDEO_SOURCES.AI_VEO;
  const showKlingOptions =
    config.media_type === 'videos' && config.video_source === VIDEO_SOURCES.AI_KLING;
  const showViduOptions =
    config.media_type === 'videos' && config.video_source === VIDEO_SOURCES.AI_VIDU;
  const showSeedance2Options =
    config.media_type === 'videos' && config.video_source === VIDEO_SOURCES.AI_SEEDANCE2;

  const showDeforumOptions =
    config.media_type === 'videos' && config.video_source === VIDEO_SOURCES.AI_DEFORUM;

  // Show AI Video sub-panel when the AI Videos category is active
  const showAIVideoPanel = config.media_type === 'videos' && activeCategory === 'ai_video';

  // Show AI Director info when that category is active
  const showAIDirectorInfo = config.media_type === 'videos' && activeCategory === 'ai_director';

  // Show stock provider dropdown only for stock videos
  const showStockProviderDropdown =
    config.media_type === 'videos' && config.video_source === VIDEO_SOURCES.STOCK;

  // I2V: detect if selected video_source requires a first-frame image
  const currentVideoSourceInfo =
    config.media_type === 'videos' ? VIDEO_SOURCE_INFO[config.video_source as VideoSource] : null;
  const isI2VSource = Boolean(currentVideoSourceInfo?.requiresImage);
  const showI2VImagePanel = isI2VSource;

  // Also show Fooocus panel in I2V mode when Fooocus is the first-frame generator.
  const showFooocusOptionsForI2V = isI2VSource && config.image_provider_for_video === 'fooocus';

  // Combined gate used in JSX below (images-only AI mode OR I2V with Fooocus).
  const showFooocusPanel = showFooocusOptions || showFooocusOptionsForI2V;

  // ComfyUI SD 1.5 panel gate (mirrors Fooocus pattern).
  const showComfyuiOptions =
    config.media_type === 'images' &&
    config.image_source === 'ai' &&
    config.image_provider === 'comfyui-sd';
  const showComfyuiOptionsForI2V = isI2VSource && config.image_provider_for_video === 'comfyui-sd';
  const showComfyuiPanel = showComfyuiOptions || showComfyuiOptionsForI2V;

  // PROVIDER_FEATURES-driven gating for the AI-image subpanels (mirrors the
  // model-aware composition in ImageConfigForm / ImageGeneratorPanel). The
  // active AI provider is the I2V first-frame generator when an I2V source is
  // selected, otherwise the AI-images-mode provider. We resolve its feature
  // flags so the Fooocus / ComfyUI subpanels show/hide their internal Style and
  // Model blocks from `supportsStyles` / `supportsModels` instead of assuming
  // them by provider id. NOTE: this only refines the INTERNAL blocks of the two
  // subpanels — the provider→subpanel routing above is unchanged, so cloud
  // providers (flux/seedream/qwen/openrouter/...) still render NO subpanel.
  const activeAiProvider = (
    showI2VImagePanel ? config.image_provider_for_video : config.image_provider
  ) as AIImageProvider | null;
  const activeFeatures = activeAiProvider ? getProviderFeatures(activeAiProvider) : null;

  // Image provider availability for the I2V sub-panel
  const { availability: imgAvailability } = useImageProviderAvailability();

  // Auto-populate image_provider_for_video with "fooocus" when user selects an I2V source
  useEffect(() => {
    if (isI2VSource && !config.image_provider_for_video) {
      onChange({ ...config, image_provider_for_video: 'fooocus' });
    }
  }, [isI2VSource, config.image_provider_for_video]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleI2VImageProviderChange = useCallback(
    (providerId: string) => {
      onChange({ ...config, image_provider_for_video: providerId });
    },
    [config, onChange]
  );

  // I2V validation: block submit if I2V selected but no image provider chosen
  const i2vValidationError =
    isI2VSource && !config.image_provider_for_video
      ? t('mediaSource.i2v.imageProviderRequired', {
          defaultValue: 'An image generator is required for this I2V provider.',
        })
      : null;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Media Type Selection - Exclusive choice between Images and Videos */}
      <div className="form-group">
        <label className="form-label-icon">
          <Film className="h-4 w-4 flex-shrink-0" />
          <span>{t('mediaSource.title', { defaultValue: 'Visual Media Type' })}</span>
        </label>
        <p className="text-xs text-secondary-600 dark:text-white/70 mb-3">
          {t('mediaSource.description', {
            defaultValue: 'Choose between static images or motion videos',
          })}
        </p>

        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {/* Images Option */}
          <button
            type="button"
            data-testid="media-type-images"
            onClick={() => !disabled && handleMediaTypeChange('images')}
            disabled={disabled}
            className={`
              option-card relative border-2
              ${
                config.media_type === 'images'
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-secondary-700 hover:border-blue-300 hover:bg-blue-50/50 dark:hover:bg-blue-900/10'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {config.media_type === 'images' && (
              <div className="absolute top-2 right-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
              </div>
            )}
            <div
              className={`p-2 rounded-full mb-2 ${config.media_type === 'images' ? 'bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-white/80'}`}
            >
              <Image className="h-5 w-5" />
            </div>
            <span
              className={`text-sm font-medium ${config.media_type === 'images' ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-white'}`}
            >
              {t('mediaSource.images.label', { defaultValue: 'Static Images' })}
            </span>
            <span className="text-xs text-gray-500 dark:text-white/70 text-center mt-1">
              {t('mediaSource.images.description', { defaultValue: 'Photo slideshow style' })}
            </span>
          </button>

          {/* Videos Option */}
          <button
            type="button"
            data-testid="media-type-videos"
            onClick={() => !disabled && handleMediaTypeChange('videos')}
            disabled={disabled}
            className={`
              option-card relative border-2
              ${
                config.media_type === 'videos'
                  ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-500 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-secondary-700 hover:border-purple-300 hover:bg-purple-50/50 dark:hover:bg-purple-900/10'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {config.media_type === 'videos' && (
              <div className="absolute top-2 right-2">
                <div className="w-2 h-2 rounded-full bg-purple-500" />
              </div>
            )}
            <div
              className={`p-2 rounded-full mb-2 ${config.media_type === 'videos' ? 'bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-400' : 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-white/80'}`}
            >
              <Film className="h-5 w-5" />
            </div>
            <span
              className={`text-sm font-medium ${config.media_type === 'videos' ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-white'}`}
            >
              {t('mediaSource.videos.label', { defaultValue: 'Motion Videos' })}
            </span>
            <span className="text-xs text-gray-500 dark:text-white/70 text-center mt-1">
              {t('mediaSource.videos.description', { defaultValue: 'Video clips with movement' })}
            </span>
          </button>
        </div>
      </div>

      {/* Source Type Selection */}
      <div className="form-group">
        <label className="form-label-icon">
          <Sparkles className="h-4 w-4 flex-shrink-0" />
          <span>{t('mediaSource.sourceType', { defaultValue: 'Source Type' })}</span>
        </label>

        {config.media_type === 'images' ? (
          <ImageSourceCards
            imageSource={config.image_source}
            onImageSourceChange={handleImageSourceChange}
            disabled={disabled}
          />
        ) : (
          <VideoSourceCards
            videoSource={config.video_source}
            onVideoSourceChange={handleVideoSourceChange}
            disabled={disabled}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
          />
        )}
      </div>

      {/* Image Provider Selection - Card grid for AI, dropdown for stock */}
      {config.media_type === 'images' && config.image_source === 'ai' && (
        <ImageProviderSelector
          value={config.image_provider as AIImageProvider}
          onChange={(provider) => handleImageProviderChange(provider)}
          disabled={disabled}
        />
      )}
      {config.media_type === 'images' &&
        config.image_source === 'stock' &&
        imageProviders.length > 1 && (
          <CompactSelect
            label={t('imageSource.provider.label')}
            // This branch only ever renders STOCK_IMAGE_PROVIDERS (Pexels/Pixabay,
            // gated by image_source === 'stock' above) — both already priced
            // "Free" as a plain string, never a local-GPU provider. No local/cloud
            // discount applies here, so the pricing is shown as-is (RULE #-2: do
            // not run getEffectivePricing()/LOCAL_IMAGE_PROVIDER_IDS through this
            // list — it can never match and previously implied a local-discount
            // path that does not exist for stock providers).
            options={imageProviders.map((provider) => ({
              value: provider.value,
              label: `${t(provider.labelKey)}${provider.pricing ? ` (${provider.pricing})` : ''}`,
              disabled: !provider.enabled,
            }))}
            value={config.stock_image_provider ?? ''}
            onChange={(v) => !disabled && handleImageProviderChange(v)}
            disabled={disabled}
            testId="stock-image-provider-select"
          />
        )}

      {/* Stock Video Provider Selection */}
      {showStockProviderDropdown && STOCK_VIDEO_PROVIDERS.length > 1 && (
        <CompactSelect
          label={t('videoSource.provider.label', { defaultValue: 'Provider' })}
          options={STOCK_VIDEO_PROVIDERS.map((provider) => ({
            value: provider.value,
            label: `${t(provider.labelKey)}${provider.pricing ? ` (${provider.pricing})` : ''}`,
            disabled: !provider.enabled,
          }))}
          value={config.stock_video_provider ?? ''}
          onChange={(v) =>
            !disabled &&
            onChange({
              ...config,
              stock_video_provider: v as StockVideoProvider,
            })
          }
          disabled={disabled}
          testId="stock-video-provider-select"
        />
      )}

      {/* AI Video Provider Sub-Panel (when AI Videos category is selected) */}
      {showAIVideoPanel && (
        <div className="form-group">
          <label className="form-label-icon">
            <Sparkles className="h-4 w-4 flex-shrink-0" />
            <span>
              {t('videoSource.aiVideo.selectProvider', { defaultValue: 'Select AI Provider' })}
            </span>
          </label>
          <AIVideoProviderPanel
            value={config.video_source}
            onChange={handleVideoSourceChange}
            disabled={disabled}
          />
        </div>
      )}

      {/* I2V First-Frame Image Generator Panel */}
      {showI2VImagePanel && (
        <div className="form-group">
          <label className="form-label-icon">
            <Image className="h-4 w-4 flex-shrink-0" />
            <span>
              {t('mediaSource.i2v.imageGeneratorLabel', {
                defaultValue: 'Initial frame image generator',
              })}
            </span>
          </label>
          <p className="text-xs text-white/60 mb-3">
            {t('mediaSource.i2v.imageGeneratorDescription', {
              defaultValue:
                'This provider will generate the first frame used as the seed image for the I2V video clip.',
            })}
          </p>
          <I2VImageProviderPicker
            imgAvailability={imgAvailability}
            value={config.image_provider_for_video}
            onChange={handleI2VImageProviderChange}
            disabled={disabled}
          />
          {i2vValidationError && <p className="text-xs text-red-400 mt-2">{i2vValidationError}</p>}
        </div>
      )}

      {/* AI Director Info Panel */}
      {showAIDirectorInfo && (
        <div className="p-4 rounded-lg bg-black/20 border border-amber-500/30">
          <div className="flex items-start gap-3">
            <Brain className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-white mb-1">
                {t('videoSource.aiDirector.infoTitle', { defaultValue: 'Smart Media Selection' })}
              </h4>
              <p className="text-xs text-white/60">
                {t('videoSource.aiDirector.infoDescription', {
                  defaultValue:
                    'The AI will intelligently select the best visual media for each scene - choosing between stock videos, AI-generated images, AI video, or YouTube clips based on the content.',
                })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Fooocus Style Options (AI Images mode OR I2V with Fooocus first-frame generator) */}
      {showFooocusPanel && (
        <FooocusStyleOptions
          styleMode={config.fooocus_style_mode}
          styles={config.fooocus_styles}
          styleDescription={config.fooocus_style_description}
          fooocusModel={config.fooocus_model}
          onStyleModeChange={handleFooocusStyleModeChange}
          onStylesChange={handleFooocusStylesChange}
          onStyleDescriptionChange={handleFooocusStyleDescriptionChange}
          onFooocusModelChange={handleFooocusModelChange}
          disabled={disabled}
          showStyles={activeFeatures?.supportsStyles ?? true}
          showModel={activeFeatures?.supportsModels ?? true}
        />
      )}

      {/* ComfyUI SD 1.5 Options (AI Images mode OR I2V with comfyui-sd first-frame generator) */}
      {showComfyuiPanel && (
        <ComfyUISDOptions
          model={config.comfyui_sd_model}
          steps={config.comfyui_sd_steps}
          loras={config.comfyui_sd_loras}
          styles={config.fooocus_styles}
          onModelChange={handleComfyuiModelChange}
          onStepsChange={handleComfyuiStepsChange}
          onLorasChange={handleComfyuiLorasChange}
          onStylesChange={handleFooocusStylesChange}
          disabled={disabled}
          showStyles={activeFeatures?.supportsStyles ?? true}
          showModel={activeFeatures?.supportsModels ?? true}
        />
      )}

      {/* Suggested quality + negative prompt — shared with the image tab.
          Shown whenever an SD image provider panel (Fooocus / ComfyUI-SD) is
          active, covering both AI-images mode and the I2V first-frame generator.
          Bounded to those providers because only their backend paths consume
          image_negative_prompt / image_use_suggested_quality. */}
      {(showFooocusPanel || showComfyuiPanel) && (
        <AIImagePromptOptions
          negativePrompt={config.image_negative_prompt}
          onNegativePromptChange={(v) => onChange({ ...config, image_negative_prompt: v })}
          useSuggestedQuality={config.image_use_suggested_quality}
          onUseSuggestedQualityChange={(v) =>
            onChange({ ...config, image_use_suggested_quality: v })
          }
          disabled={disabled}
          negativeHint={t('mediaSource.aiImage.negativeHint', {
            defaultValue:
              'Added to every generated scene image, on top of the per-scene negatives the AI writes automatically.',
          })}
        />
      )}

      {/* Veo Options */}
      {showVeoOptions && (
        <VeoOptionsPanel
          veoConfig={config.veo_config}
          estimatedCost={estimatedVeoCost}
          durationMinutes={durationMinutes}
          showAdvanced={showAdvancedOptions}
          onToggleAdvanced={() => setShowAdvancedOptions(!showAdvancedOptions)}
          onChange={handleVeoConfigChange}
          disabled={disabled}
        />
      )}

      {/* Kling Options */}
      {showKlingOptions && (
        <KlingOptionsPanel
          klingConfig={config.kling_config ?? DEFAULT_KLING_CONFIG}
          estimatedCost={estimatedKlingCost}
          durationMinutes={durationMinutes}
          showAdvanced={showAdvancedOptions}
          onToggleAdvanced={() => setShowAdvancedOptions(!showAdvancedOptions)}
          onChange={handleKlingConfigChange}
          disabled={disabled}
        />
      )}

      {/* Vidu Options */}
      {showViduOptions && (
        <ViduOptionsPanel
          viduConfig={config.vidu_config ?? DEFAULT_VIDU_CONFIG}
          durationMinutes={durationMinutes}
          showAdvanced={showAdvancedOptions}
          onToggleAdvanced={() => setShowAdvancedOptions(!showAdvancedOptions)}
          onChange={handleViduConfigChange}
          disabled={disabled}
        />
      )}

      {showSeedance2Options && (
        <Seedance2OptionsPanel
          seedance2Config={config.seedance2_config ?? DEFAULT_SEEDANCE2_CONFIG}
          estimatedCost={0}
          durationMinutes={durationMinutes}
          showAdvanced={showAdvancedOptions}
          onToggleAdvanced={() => setShowAdvancedOptions(!showAdvancedOptions)}
          onChange={handleSeedance2ConfigChange}
          disabled={disabled}
        />
      )}

      {/* Deforum Schedule Editor (when ai_deforum source is selected) */}
      {showDeforumOptions && (
        <DeforumScheduleEditor
          config={config.deforum_config ?? DEFAULT_DEFORUM_CONFIG}
          onChange={handleDeforumConfigChange}
          disabled={disabled}
          targetResolution={resolution}
        />
      )}
    </div>
  );
});

export default MediaSourceSelector;
